import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { PhotoCamera } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useEditUserMutation } from '../../../app/Slices/SevenDayApiSlice';
import FormUpload from '../../shared/FormUpload';
import CustomTooltip from '../../shared/CustomTooltip';
import toast from '../../shared/Toast';
import ProfilePicture from '../Profile/ProfilePicture';
import ProfilePictureModal from '../Profile/Form/Profile Picture/ProfilePictureModal';
import RoleChip from '../../shared/RoleChip';
import { capitalizeName } from '../../../shared/utils';
import type { Media } from '../../types/user';

const formatDisplayDate = (value: unknown) => {
    if (value == null || value === '') return null;
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

const formatLastLoginAtDisplay = (value: unknown) => {
    if (value == null || value === '') return null;
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

type ProfilePhotoPickForm = { profilePhotoPick: Media[] };

function ManagedUserPhotoDropZone({
    onFileChosen,
    fileInputRef,
}: {
    onFileChosen: (file: File) => void;
    fileInputRef?: React.MutableRefObject<HTMLInputElement | null>;
}) {
    const methods = useForm<ProfilePhotoPickForm>({
        defaultValues: { profilePhotoPick: [] },
    });

    const onUpload = useCallback(
        async (files: File[]): Promise<Media[] | undefined> => {
            const file = files[0];
            if (file) {
                onFileChosen(file);
                return [];
            }
            return undefined;
        },
        [onFileChosen],
    );

    return (
        <FormProvider {...methods}>
            <Box sx={{ width: '100%' }}>
                <FormUpload
                    name='profilePhotoPick'
                    control={methods.control}
                    onUpload={onUpload}
                    showFiles={false}
                    compact
                    validation={{
                        acceptedTypes: {
                            'image/png': ['.png', '.jpg', '.jpeg'],
                        },
                        maxFileSize: 5 * 1024 * 1024,
                        maxFiles: 1,
                    }}
                    uploadText='Click to browse files or drag and drop'
                    helperText='PNG or JPG up to 5MB'
                    hasLightBox={false}
                    fileInputRef={fileInputRef}
                />
            </Box>
        </FormProvider>
    );
}

interface UserSummaryProps {
    /** Undefined while parent query is loading or skipped. */
    user?: {
        id?: number;
        firstName?: string;
        lastName?: string;
        email?: string;
        phone?: string;
        role: string;
        viewOnly?: boolean;
        isActive?: boolean;
        lastLoginAt?: string | Date | null;
        profilePicture?: {
            src: string;
            id?: string;
        };
        department?: {
            name: string;
        };
    };
    /** Same URL used by `useGetIndividualUserInfoQuery` (PATCH target for photo). */
    userUrl?: string;
    canEditPhoto?: boolean;
}

const UserSummary = ({
    user,
    userUrl,
    canEditPhoto = false,
}: UserSummaryProps) => {
    if (user == null) {
        return null;
    }

    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();
    const [photoEditActive, setPhotoEditActive] = useState(false);
    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [pickedPhoto, setPickedPhoto] = useState<File | undefined>();
    const [editUser] = useEditUserMutation();
    const photoFileInputRef = useRef<HTMLInputElement | null>(null);
    const mobileFileInputRef = useRef<HTMLInputElement>(null);

    const teamLabel = user?.department?.name
        ? capitalizeName(user.department.name)
        : null;

    const lastLoginRaw = user?.lastLoginAt;
    const lastLoggedInDate = formatDisplayDate(lastLoginRaw);
    const lastLoggedInAtWithTime = formatLastLoginAtDisplay(lastLoginRaw);

    const isActive = user?.isActive === true;
    const statusChip = (
        <Chip
            label={isActive ? 'Enabled' : 'Disabled'}
            size='small'
            sx={{
                flexShrink: 0,
                fontWeight: 600,
                bgcolor: isActive
                    ? theme.palette.textAccent.contrastText
                    : 'none',
                color: isActive
                    ? theme.palette.textAccent.main
                    : 'text.secondary',
                borderColor: theme.palette.textAccent.main,
            }}
        />
    );

    const accountRowStackProps = {
        direction: 'row' as const,
        alignItems: 'center' as const,
        justifyContent: {
            xs: 'space-between',
            sm: 'flex-start',
        } as const,
        flexWrap: { xs: 'nowrap', sm: 'wrap' } as const,
        gap: { xs: 0, sm: 1.5 },
        sx: { width: '100%' },
    };

    const profileAccountRows = (
        <Stack
            spacing={1.75}
            sx={{
                minWidth: { xs: 0, sm: 200 },
                maxWidth: { xs: 'none', sm: 280 },
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
            }}
        >
            <Stack {...accountRowStackProps}>
                <Typography
                    variant='body2'
                    component='span'
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        flexShrink: 0,
                    }}
                >
                    Account status
                </Typography>
                {statusChip}
            </Stack>
            {lastLoggedInDate ? (
                <Stack {...accountRowStackProps}>
                    <Typography
                        variant='body2'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            flexShrink: 0,
                        }}
                    >
                        Last logged in at
                    </Typography>
                    <Typography
                        variant='body2'
                        component='span'
                        fontWeight={600}
                        sx={{
                            color: 'text.primary',
                            textAlign: { xs: 'right', sm: 'left' },
                            minWidth: 0,
                        }}
                    >
                        {isMobile ? lastLoggedInAtWithTime : lastLoggedInDate}
                    </Typography>
                </Stack>
            ) : (
                <Stack {...accountRowStackProps}>
                    <Typography
                        variant='body2'
                        component='span'
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            flexShrink: 0,
                        }}
                    >
                        Last logged in at
                    </Typography>
                    <Typography
                        variant='body2'
                        component='span'
                        fontWeight={600}
                        sx={{ color: 'text.secondary' }}
                    >
                        —
                    </Typography>
                </Stack>
            )}
        </Stack>
    );

    const isMobilePhotoEditRow = isMobile && canEditPhoto && photoEditActive;

    const rowJustify = isMobilePhotoEditRow
        ? ('flex-start' as const)
        : ({ xs: 'center', sm: 'flex-start' } as const);
    const iconSx = {
        fontSize: 12,
        width: 12,
        height: 12,
        color: 'text.secondary',
        flexShrink: 0,
    } as const;

    const nameTeamEmailBlock = (
        <Stack
            alignItems={
                isMobilePhotoEditRow
                    ? 'flex-start'
                    : { xs: 'center', sm: 'flex-start' }
            }
            spacing={isMobilePhotoEditRow ? 0.5 : isMobile ? 1.25 : 0.5}
            sx={{
                textAlign: isMobilePhotoEditRow
                    ? 'left'
                    : { xs: 'center', sm: 'left' },
                minWidth: 0,
                flexShrink: 0,
                ...(isMobilePhotoEditRow ? { flex: 1 } : {}),
            }}
        >
            {isMobile && !isMobilePhotoEditRow ? (
                <>
                    <Typography
                        variant='h5'
                        sx={{
                            fontWeight: 700,
                            color: 'primary.dark',
                            textAlign: 'center',
                            lineHeight: 1.2,
                        }}
                    >
                        {user?.firstName} {user?.lastName}
                    </Typography>
                    <RoleChip
                        user={{
                            ...user,
                            viewOnly: user.viewOnly ?? false,
                        }}
                    />
                </>
            ) : (
                <Stack
                    direction='row'
                    alignItems='center'
                    flexWrap='wrap'
                    gap={1}
                    justifyContent='flex-start'
                >
                    <Typography
                        variant='h5'
                        sx={{
                            fontWeight: 700,
                            color: 'primary.dark',
                        }}
                    >
                        {user?.firstName} {user?.lastName}
                    </Typography>
                    <RoleChip
                        user={{
                            ...user,
                            viewOnly: user.viewOnly ?? false,
                        }}
                    />
                </Stack>
            )}
            <Stack
                spacing={0.75}
                alignItems={
                    isMobilePhotoEditRow
                        ? 'flex-start'
                        : { xs: 'center', sm: 'flex-start' }
                }
                sx={{
                    width: '100%',
                    pt: isMobilePhotoEditRow ? 0.25 : isMobile ? 0.5 : 0.25,
                }}
            >
                {teamLabel ? (
                    <Stack
                        direction='row'
                        alignItems='center'
                        gap={1.25}
                        justifyContent={rowJustify}
                        sx={{ width: '100%' }}
                    >
                        <GroupOutlinedIcon sx={iconSx} aria-hidden />
                        <Typography variant='body2' color='text.secondary'>
                            {teamLabel}
                        </Typography>
                    </Stack>
                ) : null}
                <Stack
                    direction='row'
                    alignItems='center'
                    gap={1.25}
                    justifyContent={rowJustify}
                    sx={{ width: '100%' }}
                >
                    <MailOutlineOutlinedIcon sx={iconSx} aria-hidden />
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ wordBreak: 'break-word' }}
                    >
                        {user?.email?.trim() ? user.email : '—'}
                    </Typography>
                </Stack>
                {user?.phone?.trim() ? (
                    <Stack
                        direction='row'
                        alignItems='center'
                        gap={1.25}
                        justifyContent={rowJustify}
                        sx={{ width: '100%' }}
                    >
                        <PhoneOutlinedIcon sx={iconSx} aria-hidden />
                        <Typography
                            variant='body2'
                            color='text.secondary'
                            sx={{ wordBreak: 'break-word' }}
                        >
                            {user.phone.trim()}
                        </Typography>
                    </Stack>
                ) : null}
            </Stack>
        </Stack>
    );

    const desktopAccountAside = (pinEnd = true) => (
        <Stack
            direction='row'
            alignItems='center'
            spacing={2.5}
            sx={{
                flexShrink: 0,
                ...(pinEnd ? { ml: 'auto', pl: 1 } : {}),
            }}
        >
            <Box
                sx={{
                    width: '1px',
                    alignSelf: 'stretch',
                    bgcolor: theme.palette.custom.modalActionBorder,
                }}
            />
            {profileAccountRows}
        </Stack>
    );

    const closePhotoModal = useCallback(() => {
        setPhotoModalOpen(false);
        setPickedPhoto(undefined);
    }, []);

    const onDropZoneFile = useCallback((file: File) => {
        setPickedPhoto(file);
        setPhotoModalOpen(true);
    }, []);

    const openMobilePhotoPicker = useCallback(() => {
        mobileFileInputRef.current?.click();
    }, []);

    const onMobilePhotoInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) {
                onDropZoneFile(file);
            }
        },
        [onDropZoneFile],
    );

    const beginPhotoEdit = useCallback(() => {
        setPhotoEditActive(true);
        if (isMobile) {
            setTimeout(() => openMobilePhotoPicker(), 0);
        }
    }, [isMobile, openMobilePhotoPicker]);

    const exitPhotoEdit = useCallback(() => {
        closePhotoModal();
        setPhotoEditActive(false);
    }, [closePhotoModal]);

    const handlePhotoSaved = useCallback(
        async (media: Media) => {
            if (!userUrl) {
                closePhotoModal();
                return;
            }
            try {
                await editUser([
                    userUrl,
                    { profilePicture: { id: media.id } },
                ]).unwrap();
                toast.success('Profile photo updated');
                setPhotoEditActive(false);
                closePhotoModal();
            } catch {
                toast.error('Could not save profile photo for this user');
            }
        },
        [userUrl, editUser, closePhotoModal],
    );

    const inPhotoEditLayout = Boolean(canEditPhoto && photoEditActive);

    const avatarWithControls = (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <ProfilePicture
                profileDetails={{
                    firstName: user.firstName ?? '',
                    lastName: user.lastName ?? '',
                    email: user.email ?? '',
                }}
                src={user?.profilePicture?.src}
                sx={{
                    width: { xs: 140, sm: 140 },
                    height: { xs: 140, sm: 140 },
                    fontSize: { xs: '2.5rem', sm: '2rem' },
                }}
            />
            {canEditPhoto && !photoEditActive && (
                <CustomTooltip
                    title={
                        <Typography>
                            Profile picture may be used for facial recognition
                            and is required for some experiences.
                        </Typography>
                    }
                    placement='right'
                >
                    <Box
                        component='span'
                        sx={{
                            position: 'absolute',
                            right: { xs: 2, sm: 4 },
                            bottom: { xs: 2, sm: 4 },
                            lineHeight: 0,
                        }}
                    >
                        <IconButton
                            size='small'
                            type='button'
                            onClick={beginPhotoEdit}
                            sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 1,
                                width: { xs: 34, sm: 36 },
                                height: { xs: 34, sm: 36 },
                                '&:hover': {
                                    bgcolor: 'background.paper',
                                },
                            }}
                            aria-label='Edit profile photo'
                        >
                            <PhotoCamera
                                sx={{
                                    fontSize: { xs: 17, sm: 18 },
                                    color: 'text.secondary',
                                }}
                            />
                        </IconButton>
                    </Box>
                </CustomTooltip>
            )}
            {canEditPhoto &&
                photoEditActive &&
                (isMobile ? (
                    <Box
                        component='span'
                        sx={{
                            position: 'absolute',
                            right: { xs: 2, sm: 4 },
                            bottom: { xs: 2, sm: 4 },
                            lineHeight: 0,
                        }}
                    >
                        <IconButton
                            size='small'
                            type='button'
                            onClick={openMobilePhotoPicker}
                            sx={{
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                boxShadow: 1,
                                width: { xs: 34, sm: 36 },
                                height: { xs: 34, sm: 36 },
                                '&:hover': {
                                    bgcolor: 'background.paper',
                                },
                            }}
                            aria-label='Change profile photo'
                        >
                            <PhotoCamera
                                sx={{
                                    fontSize: { xs: 17, sm: 18 },
                                    color: 'text.secondary',
                                }}
                            />
                        </IconButton>
                    </Box>
                ) : (
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <PhotoCamera
                            sx={{ fontSize: 18, color: 'text.secondary' }}
                        />
                    </Box>
                ))}
        </Box>
    );

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                overflow: 'hidden',
            }}
        >
            {inPhotoEditLayout && (
                <Stack
                    direction='row'
                    alignItems='flex-start'
                    spacing={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        pb: { xs: 1.5, sm: 2 },
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box
                        component={isMobile ? 'button' : 'div'}
                        type={isMobile ? 'button' : undefined}
                        onClick={isMobile ? openMobilePhotoPicker : undefined}
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            bgcolor: (t) =>
                                t.palette.mode === 'dark'
                                    ? 'action.selected'
                                    : alpha(t.palette.primary.main, 0.12),
                            color: 'primary.main',
                            border: 'none',
                            p: 0,
                            cursor: isMobile ? 'pointer' : 'default',
                        }}
                        aria-label={
                            isMobile ? 'Change profile photo' : undefined
                        }
                    >
                        <PhotoCamera sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography
                            component='h2'
                            variant='h6'
                            sx={{
                                fontWeight: 700,
                                color: 'primary.dark',
                                lineHeight: 1.3,
                            }}
                        >
                            Profile photo
                        </Typography>
                        <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            Upload a clear photo of the user&apos;s face. PNG or
                            JPG up to 5MB.
                        </Typography>
                    </Box>
                </Stack>
            )}
            <Box
                sx={{
                    p: { xs: 2, sm: 3 },
                    width: '100%',
                    display: 'flex',
                    gap: 3,
                    ...(inPhotoEditLayout && !isMobile
                        ? {
                              flexDirection: 'row',
                              alignItems: 'center',
                          }
                        : inPhotoEditLayout
                          ? {
                                flexDirection: 'column',
                                alignItems: 'stretch',
                            }
                          : {
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'center', sm: 'center' },
                                gap: { xs: 2.5, sm: 3 },
                            }),
                }}
            >
                {inPhotoEditLayout && !isMobile ? (
                    <Stack
                        direction='row'
                        alignItems='center'
                        spacing={3}
                        sx={{ width: '100%', minWidth: 0 }}
                    >
                        {avatarWithControls}
                        {nameTeamEmailBlock}
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '100%',
                                display: 'flex',
                                alignItems: 'stretch',
                                alignSelf: 'stretch',
                            }}
                        >
                            <ManagedUserPhotoDropZone
                                onFileChosen={onDropZoneFile}
                                fileInputRef={photoFileInputRef}
                            />
                        </Box>
                        {desktopAccountAside(false)}
                    </Stack>
                ) : inPhotoEditLayout && isMobile ? (
                    <>
                        <Stack
                            spacing={3}
                            alignItems='stretch'
                            sx={{ width: '100%' }}
                        >
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                                sx={{ width: '100%' }}
                            >
                                {avatarWithControls}
                                {nameTeamEmailBlock}
                            </Stack>
                        </Stack>
                        <Divider sx={{ width: '100%' }} />
                        {profileAccountRows}
                    </>
                ) : (
                    <>
                        {avatarWithControls}
                        <Box
                            sx={{
                                flex: { xs: 'none', sm: 1 },
                                minWidth: 0,
                                width: { xs: '100%', sm: 'auto' },
                                display: 'flex',
                                justifyContent: {
                                    xs: 'center',
                                    sm: 'flex-start',
                                },
                            }}
                        >
                            {nameTeamEmailBlock}
                        </Box>
                        {!isMobile && desktopAccountAside()}
                        {isMobile && (
                            <>
                                <Divider
                                    sx={{
                                        width: '100%',
                                        borderColor:
                                            theme.palette.custom
                                                .modalActionBorder,
                                    }}
                                />
                                {profileAccountRows}
                            </>
                        )}
                    </>
                )}
            </Box>

            {inPhotoEditLayout && (
                <Stack
                    direction='row'
                    justifyContent='flex-end'
                    alignItems='center'
                    spacing={1}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{
                        p: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 2 },
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        type='button'
                        variant='outlined'
                        size='small'
                        onClick={exitPhotoEdit}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'divider',
                            color: 'text.primary',
                        }}
                    >
                        Cancel
                    </Button>
                    {!isMobile && (
                        <Button
                            type='button'
                            variant='contained'
                            size='small'
                            onClick={() => photoFileInputRef.current?.click()}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Save
                        </Button>
                    )}
                </Stack>
            )}

            {canEditPhoto && (
                <ProfilePictureModal
                    open={photoModalOpen}
                    onClose={closePhotoModal}
                    onSubmit={handlePhotoSaved}
                    photo={pickedPhoto}
                />
            )}
            {canEditPhoto && isMobile && (
                <input
                    ref={mobileFileInputRef}
                    type='file'
                    accept='image/png,image/jpeg,image/jpg'
                    hidden
                    onChange={onMobilePhotoInputChange}
                />
            )}
        </Paper>
    );
};

export default UserSummary;
